import classes from './DescriptionPageTemplate.module.css';
import { useState } from 'react';
import jsonFormat from 'json-format';
import ApiCallEditor from '../../UI/ApiCallEditor';
import ParamsTable from './ParamsTable';
import DocHeader from './DocHeader';
import EndpointCard from './EndpointCard';
import RequestExample from './RequestExample';
import { JSON_INDENT } from './JsonCode';
import ResponseExamples from './ResponseExamples';
import DocToc from './DocToc';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-chrome';
import 'ace-builds/src-noconflict/ext-language_tools';
import { simpleSubmitHandler } from '../../../util/HelperFunctions';
import AccordionSideNav from '../../layout/SideNav/AccordionSideNav/AccordionSideNav';
import config from '../../../store/config';


/**
 * json데이터를 json형태의 문자열로 반환하는 함수
 */
function jsonFormatIfExist(value) {
  return (value && jsonFormat(value, JSON_INDENT)) || '';
}

/**
 * Open API 문서 상세 페이지 컴포넌트
 */
const DescriptionPageTemplate = (props) => {
  const [versionIndex, setVersionIndex] = useState(0); 
  const pageData = props.pageDataRaw.data[versionIndex];

  const [bodyExampleTabIndex, setBodyExampleTabIndex] = useState(0);
  const [headerJsonString, setHeaderJsonString] = useState(jsonFormatIfExist(pageData.header_example));
  const [bodyJsonString, setBodyJsonString] = useState(jsonFormatIfExist(pageData.body_example));
  // API TEST 의 응답 영역은 성공 예시로 시작하고 Send 하면 실제 응답으로 덮인다.
  // 오류·콜백 예시는 조작 대상이 아니므로 문서의 Response 섹션이 담당한다
  const [responseJsonString, setResponseJsonString] = useState(jsonFormatIfExist(pageData.response_example));
  const [isLoading, setIsLoading] = useState(false);
  const [requestUrl, setRequestUrl] = useState(pageData.url);

  const onVerChange = (e) => {
    setVersionIndex(e.target.value);
    const updated_doc_data = props.pageDataRaw.data[e.target.value];
    setHeaderJsonString(jsonFormatIfExist(updated_doc_data.header_example));
    setBodyJsonString(jsonFormatIfExist(updated_doc_data.body_example));
    setBodyExampleTabIndex(0);
    setResponseJsonString(jsonFormatIfExist(updated_doc_data.response_example));
  }
  
  /**
   * Open API 테스트 호출 처리 함수
   */
  async function handleTestRequest() {
    setIsLoading(true);
    let request_url = `/api${requestUrl}`;
    const requestOption = {
      method: pageData.method,
      headers: {...JSON.parse(headerJsonString), 'Content-Type': 'application/json'},
      credentials: 'include'
    };
    if (pageData.method !== 'GET') {
      requestOption.body = bodyJsonString
    }

    simpleSubmitHandler(setIsLoading, request_url, requestOption, res_json => setResponseJsonString(jsonFormat(res_json, JSON_INDENT)));
  }

  /**
   * 문서 섹션에 노출할 응답 예시.
   * API TEST 의 state 를 쓰지 않고 pageData 에서 직접 만든다.
   * 그 state 는 Send 로 실제 응답에 덮어써지므로 문서의 정본이 될 수 없다.
   */
  const docResponseExample = jsonFormatIfExist(pageData.response_example);
  const docErrorExample = jsonFormatIfExist(pageData.error_example);
  const docCallbackExample = jsonFormatIfExist(pageData.callback_example);
  const hasResponseExample = Boolean(docResponseExample || docErrorExample || docCallbackExample);

  // 요청 관련 표와 그 라벨. 기존 라벨 규칙을 유지한다
  const REQUEST_SECTION_LABELS = {
    Header: 'Header',
    Body: 'Body Parameters',
    Query: 'Query Parameters',
    Parameter: 'URL Parameters',
  };
  const requestSections = Object.keys(REQUEST_SECTION_LABELS)
    .filter(key => pageData[key.toLowerCase()])
    .map(key => ({ key, label: REQUEST_SECTION_LABELS[key] }));

  // 요청 예시도 문서의 정본이라 API TEST 의 state 가 아니라 pageData 에서 직접 읽는다
  const hasRequestExample = Boolean(pageData.body_example);

  // 목차는 실제로 렌더된 섹션에서 만든다. 문서마다 있는 섹션이 다르다
  const tocItems = [
    { id: 'description', label: 'Description' },
    ...(requestSections.length
      ? [
          { id: 'request', label: 'Request' },
          ...requestSections.map(({ key, label }) => ({ id: `${key}-table`, label, sub: true })),
          ...(hasRequestExample
            ? [{ id: 'request-example', label: 'Request Example', sub: true }]
            : []),
        ]
      : []),
    ...(pageData.response || hasResponseExample ? [{ id: 'response', label: 'Response' }] : []),
    { id: 'api-test', label: 'API TEST' },
  ];

  return (
    <>
      <AccordionSideNav data={props.filteredPageDataList} />
      <div className={classes.container}>
        <div className={classes.docMain}>
        <DocHeader
          title={props.pageDataRaw.pageTitle || props.pageDataRaw.title}
          docTitle={props.pageDataRaw.title}
          versions={props.pageDataRaw.data}
          versionIndex={versionIndex}
          onVersionChange={onVerChange}
          filteredPageDataList={props.filteredPageDataList}
        />
        <EndpointCard
          method={pageData.method}
          url={pageData.url}
          baseUrl={config.backendUrl}
          headers={pageData.header}
        />
        <div id='description' className={classes.section}>
          <h4 className={classes['sub-heading']}>Description</h4>
          {pageData.desc}
        </div>

        {/* 요청 관련 표를 한 섹션으로 묶는다. 기존 앵커 id 는 SearchModal 계약이라 유지한다 */}
        {requestSections.length > 0 && (
          <div id='request' className={classes.section}>
            <h3 className={classes.groupHeading}>Request</h3>
            {requestSections.map(({ key, label }) => (
              <div id={`${key}-table`} className={classes.subsection} key={key}>
                <h4 className={classes['sub-heading']}>{label}</h4>
                <ParamsTable data={pageData[key.toLowerCase()]} />
              </div>
            ))}
            {/* 파라미터 표와 같은 섹션에 둬 스키마와 예시를 대조할 때 API TEST 까지 내려가지 않게 한다 */}
            {hasRequestExample && (
              <div id='request-example' className={classes.subsection}>
                <RequestExample bodyExample={pageData.body_example} />
              </div>
            )}
          </div>
        )}

        {/* 응답 표와 예시를 함께 둬 스키마와 예시를 한 화면에서 대조할 수 있게 한다 */}
        {(pageData.response || hasResponseExample) && (
          <div id='response' className={classes.section}>
            <h3 className={classes.groupHeading}>Response</h3>
            {pageData.response && (
              <div id='Response-table' className={classes.subsection}>
                <ParamsTable data={pageData.response} />
              </div>
            )}
            <ResponseExamples
              success={docResponseExample}
              error={docErrorExample}
              callback={docCallbackExample}
            />
          </div>
        )}
        <div id='api-test' className={classes.section + ' ' + classes['api-test']}>
          <h3 className={classes.groupHeading}>API TEST</h3>
          <div className={classes.url}>
            {/* MEHTOD 삽입 */}
            <div className={classes[pageData.method]}>
              <div className={classes.method}>{pageData.method}</div>
            </div>
            {/* api 주소 입력 영역 */}
            <div className={classes.input}>
              <div>
                {config.backendUrl + '/api'}
              </div>
              <input className='focus-highlight' type='text' value={requestUrl} onChange={e => setRequestUrl(e.target.value)} />
            </div>
            <button onClick={handleTestRequest} className={`${classes.send}`} disabled={isLoading}>Send</button>
          </div>
          <div className={classes.editor}>
            <h6 className={classes.header}>Header</h6>
              <ApiCallEditor 
                onChange={value => setHeaderJsonString(value)}
                value={headerJsonString}
                height='100px'
              />
          </div>
          {
            pageData.body_example && 
            <div className={classes.editor}>
              <h6 className={classes.header}>Parameter</h6>
              {Array.isArray(pageData.body_example)
                ? (() => {
                    // _tabName 필드는 에디터에 노출하지 않음
                    const { _tabName, ...rest } = pageData.body_example[bodyExampleTabIndex] || {};
                    return <>
                      <div className={classes['tab-group']} style={{ marginBottom: '10px' }}>
                        {pageData.body_example.map((item, i) =>
                          <button key={i} onClick={() => setBodyExampleTabIndex(i)} className={bodyExampleTabIndex === i ? classes.active : ''}>
                            {item._tabName || `Example ${i + 1}`}
                          </button>
                        )}
                      </div>
                      <ApiCallEditor value={jsonFormat(rest, JSON_INDENT)} />
                    </>;
                  })()
                : <ApiCallEditor
                    onChange={value => setBodyJsonString(value)}
                    value={bodyJsonString}
                  />
              }
            </div>
          }
          <div className={classes.editor}>
            <div className={classes.header}>
              <h6>Response</h6>
              <span className={classes.editorMeta}>Send 하면 실제 응답으로 교체된다</span>
            </div>
            <ApiCallEditor
              onChange={value => setResponseJsonString(value)}
              value={responseJsonString}
            />
          </div>
        </div>
        </div>
        <DocToc items={tocItems} />
      </div>
    </>
  );
};

export default DescriptionPageTemplate;
